import React, { useEffect, useRef, useState } from 'react';
import MessageContentRenderer from './MessageContentRenderer';

// Session workspace wired to the real backend. The visual shell mirrors the
// elevated Trouve reference while messages, task suggestions, connection
// state, and browser frames continue to come from live application state.

const FALLBACK_URL = 'system.netsuite.com/app/accounting/transactions/journal.nl';
const NETSUITE_LOGIN_DISPLAY_URL = 'system.netsuite.com/pages/customerlogin.jsp';
const ONBOARDING_CALENDLY_URL = 'https://calendly.com/shafin-trouve/trouve-ai-discovery-overview';
const MODE_META = {
  netsuite: {
    label: 'NetSuite', shortLabel: 'NetSuite', mark: 'N', fallbackUrl: FALLBACK_URL,
  },
  meridian: {
    label: 'Meridian Health Exchange', shortLabel: 'Meridian', mark: 'M', fallbackUrl: '127.0.0.1:8080/index.html',
  },
  greenshield: {
    label: 'GreenShield HMO', shortLabel: 'GreenShield', mark: 'G', fallbackUrl: '127.0.0.1:8095/dashboard.html',
  },
};

function calendlyEmbedUrl(user) {
  try {
    const url = new URL(ONBOARDING_CALENDLY_URL);
    const name = String(user?.name || '').trim();
    const email = String(user?.email || '').trim();
    if (name) url.searchParams.set('name', name);
    if (email) url.searchParams.set('email', email);
    url.searchParams.set('hide_event_type_details', '1');
    url.searchParams.set('hide_gdpr_banner', '1');
    return url.toString();
  } catch (_) {
    return ONBOARDING_CALENDLY_URL;
  }
}

function downloadFile(filename, text, mimeType = 'text/plain;charset=utf-8', encoding = 'utf-8') {
  try {
    let payload = text || '';
    if (String(encoding || '').toLowerCase() === 'base64') {
      const binary = atob(payload);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      payload = bytes;
    }
    const blob = new Blob([payload], { type: mimeType || 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'file.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (_) {
    // ignore
  }
}

function StreamStage({
  frameSrc,
  streamNoticeMessage,
  containerStatus,
  onRestartBrowser,
  isOpeningLiveBrowser,
  onFrameDimensions,
}) {
  if (frameSrc) {
    return (
      <div className="tv-stream-frame">
        <img
          className="tv-stream-backdrop"
          src={frameSrc}
          alt=""
          aria-hidden="true"
          draggable={false}
        />
        <img
          className="tv-replica-stream-img"
          src={frameSrc}
          alt="Live browser"
          draggable={false}
          onLoad={(event) => {
            const image = event.currentTarget;
            if (image.naturalWidth && image.naturalHeight) {
              onFrameDimensions?.({ width: image.naturalWidth, height: image.naturalHeight });
            }
          }}
        />
      </div>
    );
  }
  if (containerStatus === 'spinning_up' || !streamNoticeMessage) {
    return (
      <div className="tv-stream-state">
        <span className="tv-spinner tv-dark" />
        <div className="tv-stream-state-text">
          {containerStatus === 'spinning_up' ? 'Initializing your cloud browser…' : 'Browser is initializing…'}
        </div>
      </div>
    );
  }
  return (
    <div className="tv-stream-state">
      <div className="tv-stream-state-glyph" aria-hidden="true">⏻</div>
      <div className="tv-stream-state-text">{streamNoticeMessage}</div>
      {onRestartBrowser && (
        <button
          type="button"
          className="tv-stream-state-btn"
          onClick={onRestartBrowser}
          disabled={isOpeningLiveBrowser}
        >
          {isOpeningLiveBrowser ? 'Opening…' : 'Restart browser'}
        </button>
      )}
    </div>
  );
}

function sessionTitle(messages, sections) {
  const selectedItem = (Array.isArray(sections) ? sections : [])
    .flatMap((section) => (Array.isArray(section?.items) ? section.items : []))
    .find((item) => item?.selected && item?.title);
  if (selectedItem?.title) return String(selectedItem.title);

  const firstPrompt = (Array.isArray(messages) ? messages : [])
    .find((message) => message?.type === 'user' && typeof message?.content === 'string')
    ?.content?.trim();
  if (!firstPrompt) return 'New task';

  const firstSentence = firstPrompt.split(/[.!?\n]/)[0].trim();
  const compact = firstSentence.replace(/^please\s+/i, '').replace(/^run\s+/i, '');
  if (compact.length <= 52) return compact;
  return `${compact.slice(0, 51).trimEnd()}…`;
}

function OnboardingModal({ open, user, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="tv-onboarding-modal-layer" role="presentation" onClick={onClose}>
      <div
        className="tv-onboarding-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tv-onboarding-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="tv-onboarding-close"
          aria-label="Close onboarding scheduler"
          onClick={onClose}
        >
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <div className="tv-onboarding-head">
          <div className="tv-onboarding-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
              <path d="M3 9h18M8 2.5v4M16 2.5v4" />
              <path d="M8.5 14l2 2 4.5-4.5" />
            </svg>
          </div>
          <div className="tv-onboarding-copy">
            <span className="tv-onboarding-eyebrow">Complete account setup</span>
            <h2 id="tv-onboarding-title">Schedule your onboarding call</h2>
            <p>
              To start using Trouve, hop on a quick onboarding call with our team. Pick a time that
              works for you below and we&rsquo;ll get your account fully set up.
            </p>
          </div>
        </div>
        <div className="tv-onboarding-note" role="note">
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.3" />
            <path d="M8 7.25v3.5M8 5.25v.25" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          <span>Already booked a call? You&rsquo;re all set &mdash; please wait until the call for next steps.</span>
        </div>
        <div className="tv-onboarding-calendar-wrap">
          <iframe
            title="Schedule Trouve onboarding"
            className="tv-onboarding-calendar"
            src={calendlyEmbedUrl(user)}
          />
        </div>
      </div>
    </div>
  );
}

function ThreadMessage({ message }) {
  const { type, content, image, files } = message;

  if (type === 'file' && content && typeof content === 'object') {
    const filename = String(content.filename || 'file');
    const mimeType = String(content.mimeType || 'text/plain');
    const text = String(content.text || '');
    const encoding = String(content.encoding || 'utf-8');
    return (
      <div className="tv-msg tv-agent tv-agent-response">
        <div className="tv-msg-body">
          <button
            type="button"
            className="tv-msg-file-chip"
            onClick={() => downloadFile(filename, text, mimeType, encoding)}
            title={`Download ${filename}`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            <span className="tv-msg-file-name">{filename}</span>
            <span className="tv-msg-file-action">Download</span>
          </button>
        </div>
      </div>
    );
  }

  if (type === 'ai') {
    return (
      <div className="tv-msg tv-agent tv-agent-response">
        <div className="tv-msg-body">
          <MessageContentRenderer content={content} />
        </div>
      </div>
    );
  }

  // user
  return (
    <div className="tv-msg tv-user">
      <div className="tv-msg-body">
        {image && (
          <img src={image} alt="Uploaded" style={{ maxWidth: '100%', borderRadius: 10, display: 'block', marginBottom: content ? 8 : 0 }} />
        )}
        {files && files.length > 0 && files.map((f) => (
          <div key={String(f.id || f.filename)} className="tv-mono" style={{ fontSize: 11.5, opacity: 0.8 }}>{String(f.filename || 'file')}</div>
        ))}
        {content}
      </div>
    </div>
  );
}

function EmptyStarterIcon({ index }) {
  const icons = [
    <>
      <circle cx="7" cy="7" r="3.75" />
      <path d="m10 10 3.25 3.25" />
    </>,
    <>
      <path d="m8 2 1.15 3.35L12.5 6.5 9.15 7.65 8 11 6.85 7.65 3.5 6.5l3.35-1.15L8 2Z" />
      <path d="M12.25 10.5 13 12.75l2.25.75-2.25.75-.75 2.25-.75-2.25-2.25-.75 2.25-.75.75-2.25Z" />
    </>,
    <>
      <path d="M4 3.5h8v10H4z" />
      <path d="M6.25 6.25h3.5M6.25 8.5h3.5M6.25 10.75h2.25" />
    </>,
    <>
      <path d="M9.5 1.75 4.25 9h4l-1 5.25L12.5 7h-4l1-5.25Z" />
    </>,
  ];

  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round">
        {icons[index % icons.length]}
      </g>
    </svg>
  );
}

function EmptyThreadState({ sections = [], onSelectPrompt }) {
  const normalizedSections = (Array.isArray(sections) ? sections : [])
    .map((section) => (Array.isArray(section?.items) ? section.items : []))
    .filter((items) => items.length > 0);
  const starters = [];
  const longestSection = normalizedSections.reduce((max, items) => Math.max(max, items.length), 0);

  for (let itemIndex = 0; itemIndex < longestSection && starters.length < 4; itemIndex += 1) {
    normalizedSections.forEach((items) => {
      if (starters.length >= 4 || !items[itemIndex]) return;
      const item = items[itemIndex];
      const prompt = typeof item?.prompt === 'string' ? item.prompt : '';
      if (!prompt) return;
      starters.push({
        title: String(item?.title || prompt),
        prompt,
      });
    });
  }

  return (
    <div className="tv-empty-launch">
      <div className="tv-empty-launch-mark" aria-hidden="true">
        <svg viewBox="0 0 48 48" fill="none">
          <rect x="6.5" y="8.5" width="35" height="31" rx="11.5" />
          <path d="m17 18 6 6-6 6M27 30h7" />
          <path d="M16 8.5 19 5M32 8.5 29 5M16 39.5 13 43M32 39.5 35 43" />
        </svg>
      </div>
      <h2>What should we work on?</h2>
      {starters.length > 0 && (
        <div className="tv-empty-starters" aria-label="Suggested tasks">
          {starters.map((starter, index) => (
            <button
              type="button"
              className={`tv-empty-starter tv-empty-starter-${(index % 4) + 1}`}
              key={`${starter.title}:${index}`}
              onClick={() => onSelectPrompt?.(starter.prompt)}
              title={starter.prompt}
            >
              <span className="tv-empty-starter-icon"><EmptyStarterIcon index={index} /></span>
              <span className="tv-empty-starter-label">{starter.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarQuerySections({ sections = [], onSelectPrompt, disabled = false }) {
  if (!Array.isArray(sections) || sections.length === 0) {
    return (
      <div className="tv-side-section">
        <div className="tv-side-label">Recent</div>
        <div className="tv-task-item">
          <span className="tv-task-status tv-waiting" />
          <div className="tv-task-meta">
            <div className="tv-task-title">Loading queries</div>
            <div className="tv-task-sub">Fetching from Trouve</div>
          </div>
        </div>
      </div>
    );
  }

  const validStatuses = new Set(['running', 'waiting', 'done', 'failed']);
  return sections.map((section, sectionIndex) => {
    const label = String(section?.label || 'Queries');
    const items = Array.isArray(section?.items) ? section.items : [];
    if (items.length === 0) return null;

    return (
      <div className="tv-side-section" key={`${label}:${sectionIndex}`}>
        <div className="tv-side-label">{label}</div>
        {items.map((item, itemIndex) => {
          const title = String(item?.title || item?.prompt || 'Untitled query');
          const subtitle = String(item?.subtitle || '');
          const status = validStatuses.has(String(item?.status)) ? String(item.status) : 'waiting';
          const prompt = typeof item?.prompt === 'string' ? item.prompt : '';
          const selected = Boolean(item?.selected);
          const className = `tv-task-item${selected ? ' tv-selected' : ''}`;
          const content = (
            <>
              <span className={`tv-task-status tv-${status}`} />
              <div className="tv-task-meta">
                <div className="tv-task-title">{title}</div>
                {subtitle && <div className="tv-task-sub">{subtitle}</div>}
              </div>
            </>
          );

          if (prompt) {
            return (
              <button
                type="button"
                key={`${label}:${itemIndex}`}
                className={className}
                disabled={disabled}
                onClick={() => onSelectPrompt?.(prompt)}
                title={prompt}
              >
                {content}
              </button>
            );
          }

          return (
            <div className={className} key={`${label}:${itemIndex}`}>
              {content}
            </div>
          );
        })}
      </div>
    );
  });
}

export default function TrouveReplica({
  messages = [],
  isProcessing = false,
  isConnected = false,
  user,
  onSignOut,
  onClearChat,
  onSendMessage,
  onCancelRequest,
  netsuiteSession = null,
  agentMode = 'netsuite',
  onModeChange,
  frameSrc,
  streamNoticeMessage,
  currentPageUrl,
  containerStatus,
  onOpenLiveBrowser,
  onRestartBrowser,
  isOpeningLiveBrowser = false,
  onStreamViewportChange,
  querySections = [],
  isOnboarded = true,
  onboardingModalOpen = false,
  onCloseOnboardingModal,
}) {
  const [draft, setDraft] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const streamStageRef = useRef(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [streamDimensions, setStreamDimensions] = useState(null);

  useEffect(() => {
    if (!isProcessing) setIsCancelling(false);
  }, [isProcessing]);

  useEffect(() => {
    const stage = streamStageRef.current;
    if (!stage || !onStreamViewportChange) return undefined;

    let frameId = 0;
    let resizeTimerId = 0;
    let lastWidth = 0;
    let lastHeight = 0;
    const reportSizeNow = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const rect = stage.getBoundingClientRect();
        const width = Math.round(rect.width);
        const height = Math.round(rect.height);
        if (width <= 0 || height <= 0 || (width === lastWidth && height === lastHeight)) return;
        lastWidth = width;
        lastHeight = height;
        onStreamViewportChange({ width, height });
      });
    };

    const reportSize = () => {
      window.clearTimeout(resizeTimerId);
      resizeTimerId = window.setTimeout(reportSizeNow, 250);
    };

    reportSizeNow();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reportSize) : null;
    observer?.observe(stage);
    window.addEventListener('resize', reportSize);
    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(resizeTimerId);
      observer?.disconnect();
      window.removeEventListener('resize', reportSize);
    };
  }, [onStreamViewportChange]);

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [accountMenuOpen]);

  const avatarLetter = (user?.name || user?.email || 'S').trim().charAt(0).toUpperCase() || 'S';
  const accountName = (user?.name || user?.email || 'Account').trim();
  const modeMeta = MODE_META[agentMode] || MODE_META.netsuite;
  const urlText = !isOnboarded ? NETSUITE_LOGIN_DISPLAY_URL : currentPageUrl || modeMeta.fallbackUrl;
  const displayIsConnected = isOnboarded && isConnected;
  const currentSessionTitle = sessionTitle(messages, querySections);
  const sessionStatus = !displayIsConnected
    ? 'Connecting'
    : isProcessing
      ? 'Running'
      : messages.length > 0
        ? 'Ready'
        : 'Idle';

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isProcessing]);

  // Keep the session shell keyboard-friendly: focus the composer quickly or
  // start a fresh task with the shortcut shown in the sidebar.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        onClearChat?.();
        window.requestAnimationFrame(() => inputRef.current?.focus());
        return;
      }
      if ((e.key === '/' && document.activeElement !== inputRef.current) ||
          ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClearChat]);

  const send = () => {
    const text = draft.trim();
    if (!text || isProcessing || !displayIsConnected) return;
    onSendMessage?.(text, selectedImage?.dataUrl || null);
    setDraft('');
    setSelectedImage(null);
  };

  const selectImageFile = (file) => {
    if (!file || !file.type?.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      if (dataUrl) {
        setSelectedImage({
          dataUrl,
          name: file.name || 'image',
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const selectSidebarPrompt = (prompt) => {
    setDraft(String(prompt || ''));
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const updateStreamDimensions = ({ width, height }) => {
    setStreamDimensions((previous) => (
      previous?.width === width && previous?.height === height
        ? previous
        : { width, height }
    ));
  };

  const composer = (
    <footer className="tv-composer">
      {selectedImage && (
        <div className="tv-attachment-preview">
          <img src={selectedImage.dataUrl} alt="" className="tv-attachment-thumb" />
          <span className="tv-attachment-name tv-mono">{selectedImage.name}</span>
          <button
            type="button"
            className="tv-attachment-remove"
            aria-label="Remove attached image"
            title="Remove attached image"
            onClick={() => setSelectedImage(null)}
          >
            <svg viewBox="0 0 16 16" fill="none">
              <path d="M4.25 4.25l7.5 7.5M11.75 4.25l-7.5 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
      <div className="tv-composer-inner">
        <span className="tv-prompt-glyph tv-mono">❯</span>
        <input
          ref={inputRef}
          className="tv-composer-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); send(); }
          }}
          disabled={!displayIsConnected || isProcessing}
          placeholder={
            !displayIsConnected ? 'Connecting…'
            : isProcessing ? 'Trouve is working…'
            : 'Message Trouve'
          }
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="tv-hidden-file-input"
          onChange={(event) => {
            const file = event.target.files?.[0];
            selectImageFile(file);
            event.target.value = '';
          }}
        />
        <div className="tv-composer-right">
          <button
            type="button"
            className={`tv-attach-btn${selectedImage ? ' tv-has-attachment' : ''}`}
            aria-label="Attach image"
            title={selectedImage ? 'Replace attached image' : 'Attach image'}
            onClick={() => fileInputRef.current?.click()}
            disabled={!displayIsConnected || isProcessing}
          >
            <svg viewBox="0 0 16 16" fill="none">
              <rect x="2.25" y="3" width="11.5" height="10" rx="2" stroke="currentColor" strokeWidth="1.25" />
              <circle cx="5.25" cy="6" r="1" fill="currentColor" />
              <path d="M3.75 11.25l3.1-3.05a1.1 1.1 0 0 1 1.52-.02l1.03.96.88-.88a1.05 1.05 0 0 1 1.48 0l1.45 1.45" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M11.5 2v3M10 3.5h3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
            </svg>
          </button>
          <span
            className="tv-ctx-chip"
            title={agentMode === 'netsuite' && netsuiteSession?.userName ? `Signed in as ${netsuiteSession.userName}` : `${modeMeta.label} connection`}
          >
            <span className="tv-ctx-dot" />{modeMeta.shortLabel}
          </span>
          {isProcessing ? (
            <button
              type="button"
              className="tv-send-btn tv-composer-stop"
              title="Stop the current task"
              aria-label="Stop the current task"
              disabled={isCancelling || !onCancelRequest}
              onClick={() => {
                setIsCancelling(true);
                onCancelRequest?.();
              }}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <rect x="4.5" y="4.5" width="7" height="7" rx="1" fill="currentColor" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="tv-send-btn"
              aria-label="Send message"
              title="Send message"
              disabled={!displayIsConnected || !draft.trim()}
              onClick={send}
            >
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M8 12.75v-9.5M4.25 7 8 3.25 11.75 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </footer>
  );

  return (
    <div className="trouve-replica">
      <div className="tv-app">
        <main className="tv-columns">
          <aside className="tv-sidebar">
            <div className="tv-brand">
              <div className="tv-account" ref={accountMenuRef}>
                <button
                  type="button"
                  className="tv-brand-account"
                  title="Account"
                  aria-haspopup="menu"
                  aria-expanded={accountMenuOpen}
                  onClick={() => setAccountMenuOpen((open) => !open)}
                >
                  <span className="tv-brand-mark">T</span>
                  <span className="tv-brand-name">Trouve</span>
                  <svg className="tv-brand-caret" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M3.5 4.75 6 7.25l2.5-2.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {accountMenuOpen && (
                  <div className="tv-account-menu" role="menu">
                    <div className="tv-account-name" title={accountName}>
                      <span className="tv-account-avatar" aria-hidden="true">{avatarLetter}</span>
                      <span className="tv-account-copy">
                        {accountName}
                        {user?.email && user.email !== accountName && (
                          <span className="tv-account-email">{user.email}</span>
                        )}
                      </span>
                    </div>
                    <div className="tv-account-sep" />
                    <button
                      type="button"
                      className="tv-account-item"
                      role="menuitem"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        onSignOut?.();
                      }}
                    >
                      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M6 14H3.5A1.5 1.5 0 0 1 2 12.5v-9A1.5 1.5 0 0 1 3.5 2H6M10.5 11l3-3-3-3M13.5 8H6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Log out
                    </button>
                  </div>
                )}
              </div>
              <span className="tv-brand-version tv-mono">2.1.0</span>
            </div>

            <button className="tv-new-task-btn" onClick={onClearChat} disabled={isProcessing}>
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
              New task
              <kbd>⌘N</kbd>
            </button>

            <div className="tv-mode-picker" aria-label="Agent workspace">
              <div className="tv-side-label">Workspace</div>
              <div className="tv-mode-options" role="group" aria-label="Choose connected workspace">
                {Object.entries(MODE_META).map(([mode, meta]) => (
                  <button
                    key={mode}
                    type="button"
                    className={`tv-mode-option${agentMode === mode ? ' tv-active' : ''}`}
                    aria-pressed={agentMode === mode}
                    title={`Switch Trouve to ${meta.label}`}
                    disabled={isProcessing}
                    onClick={() => onModeChange?.(mode)}
                  >
                    <span className="tv-mode-mark">{meta.mark}</span>
                    <span>{meta.shortLabel}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="tv-sidebar-scroll">
              <SidebarQuerySections
                sections={querySections}
                onSelectPrompt={selectSidebarPrompt}
                disabled={isProcessing}
              />
            </div>

            <div className="tv-sidebar-footer">
              <div className="tv-connections">
                <div className="tv-side-label">Connections</div>
                <div className="tv-conn-row">
                  <span className="tv-conn-logo">{modeMeta.mark}</span>
                  <span className="tv-conn-copy">
                    <span className="tv-conn-name">{modeMeta.label}</span>
                    <span className="tv-conn-sub">
                      {agentMode === 'netsuite'
                        ? (netsuiteSession?.instanceName || (displayIsConnected ? 'Production' : 'Connecting…'))
                        : (displayIsConnected ? 'Demo replica' : 'Connecting…')}
                    </span>
                  </span>
                  <span className={`tv-conn-state${displayIsConnected ? ' tv-healthy' : ''}`}>
                    <span className="tv-conn-dot" />
                    {displayIsConnected ? 'Healthy' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>
          </aside>

          <section className="tv-thread">
            <header className="tv-thread-head">
              <h1 className="tv-thread-title" title={currentSessionTitle}>{currentSessionTitle}</h1>
              <span className={`tv-thread-status tv-${sessionStatus.toLowerCase()}`}>
                <span className="tv-thread-status-dot" />
                {sessionStatus}
              </span>
              <span className="tv-thread-head-spacer" />
              {isProcessing && (
                <button
                  type="button"
                  className="tv-head-action tv-danger-action"
                  disabled={isCancelling || !onCancelRequest}
                  onClick={() => {
                    setIsCancelling(true);
                    onCancelRequest?.();
                  }}
                >
                  {isCancelling ? 'Stopping…' : 'Stop'}
                </button>
              )}
            </header>

            <div
              className={`tv-thread-scroll${messages.length === 0 && !isProcessing ? ' tv-thread-empty' : ''}`}
              ref={scrollRef}
            >
              {messages.length === 0 && !isProcessing ? (
                <EmptyThreadState sections={querySections} onSelectPrompt={selectSidebarPrompt} />
              ) : (
                <>
                  <div className="tv-thread-day">Today</div>

                  {messages.map((message) => (
                    <ThreadMessage key={message.id} message={message} />
                  ))}

                  {isProcessing && (
                    <div className="tv-msg tv-agent tv-agent-response">
                      <div className="tv-msg-body tv-msg-working">
                        <span className="tv-typing-dots" aria-label="Trouve is working">
                          <i /><i /><i />
                        </span>
                        <span className="tv-working-label">Working</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          <section className="tv-stream">
            <div className="tv-browser-shell">
              <div className="tv-stream-head">
                <div className="tv-stream-title">
                  <span className={`tv-live-dot${frameSrc ? ' tv-is-live' : ''}`} />
                  Live browser
                </div>
                <div className="tv-url-pill tv-mono" title={urlText}>
                  <svg viewBox="0 0 16 16" fill="none"><rect x="3.5" y="7" width="9" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.2" /><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.2" /></svg>
                  <span>{urlText}</span>
                </div>
                <div className="tv-stream-actions">
                  <button
                    type="button"
                    className="tv-icon-btn tv-fit-btn tv-active"
                    title="Fit the full browser to the stream window"
                    aria-label="Fit the full browser to the stream window"
                    onClick={() => {
                      const rect = streamStageRef.current?.getBoundingClientRect();
                      if (rect?.width && rect?.height) {
                        onStreamViewportChange?.({ width: rect.width, height: rect.height, force: true });
                      }
                    }}
                  >
                    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M2.75 6V2.75H6M10 2.75h3.25V6M13.25 10v3.25H10M6 13.25H2.75V10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button className="tv-icon-btn tv-expand-btn" title="Open full browser" onClick={onOpenLiveBrowser} disabled={isOpeningLiveBrowser}>
                    <svg viewBox="0 0 16 16" fill="none"><path d="M9.5 2.5h4v4M6.5 13.5h-4v-4M13.5 2.5L9 7M2.5 13.5L7 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                  <button className="tv-takeover-btn" onClick={onOpenLiveBrowser} disabled={isOpeningLiveBrowser}>
                    {isOpeningLiveBrowser ? 'Opening…' : 'Take over'}
                  </button>
                </div>
              </div>

              <div className="tv-stream-stage" ref={streamStageRef}>
                <StreamStage
                  frameSrc={frameSrc}
                  streamNoticeMessage={streamNoticeMessage}
                  containerStatus={containerStatus}
                  onRestartBrowser={onRestartBrowser}
                  isOpeningLiveBrowser={isOpeningLiveBrowser}
                  onFrameDimensions={updateStreamDimensions}
                />
              </div>

              <div className="tv-stream-foot tv-mono">
                <span className="tv-stream-control-state">
                  <span className={`tv-stream-health-dot${frameSrc ? ' tv-is-live' : ''}`} />
                  {frameSrc ? 'agent controlling' : 'standby'}
                </span>
                {streamDimensions && frameSrc && (
                  <span>{streamDimensions.width} × {streamDimensions.height}</span>
                )}
                <span className="tv-foot-right">{displayIsConnected ? 'connected' : 'reconnecting…'}</span>
              </div>
            </div>
          </section>
        </main>

        {composer}

        <div className="tv-statusline tv-mono">
          <span className={`tv-status-connection${displayIsConnected ? ' tv-status-ok' : ''}`}>
            <span className="tv-status-dot" />
            {displayIsConnected ? 'connected' : 'disconnected'}
          </span>
          <span>us-west-2</span>
          <span className="tv-status-right">
            {messages.length > 0 ? `${messages.filter((message) => message.type === 'user').length} task${messages.filter((message) => message.type === 'user').length === 1 ? '' : 's'} today` : 'ready'}
          </span>
        </div>

        <OnboardingModal
          open={onboardingModalOpen}
          user={user}
          onClose={onCloseOnboardingModal}
        />
      </div>
    </div>
  );
}
